/**
 * The front page is set for an hour of the day, not for a round. These helpers name the
 * edition for the hour, say when it was set, and place the hour on the day's arc.
 */
import type { InSnipCard } from "../types";
import { sourceDisplayName } from "../../functions/_mastheads.js";
import { readMinutes } from "../../functions/_minutes.js";

export type EditionSlotKey = "morning" | "midday" | "afternoon" | "evening" | "late";

export interface EditionSlot {
  key: EditionSlotKey;
  /** The nameplate, e.g. "The Afternoon Edition". */
  name: string;
  /** One short line under the name, the page's mood for the hour. */
  line: string;
}

/** Which edition this hour belongs to (local time). */
export function editionSlot(d: Date = new Date()): EditionSlot {
  const h = d.getHours();
  if (h >= 4 && h < 11) return { key: "morning", name: "The Morning Edition", line: "What moved overnight, set for the morning" };
  if (h >= 11 && h < 14) return { key: "midday", name: "The Midday Edition", line: "The morning's stories, as they stand at noon" };
  if (h >= 14 && h < 17) return { key: "afternoon", name: "The Afternoon Edition", line: "The day so far, in one page" };
  if (h >= 17 && h < 21) return { key: "evening", name: "The Evening Edition", line: "The day, settled" };
  return { key: "late", name: "The Late Edition", line: "For the night owls: the day's best, held open" };
}

/** Where the moment sits on the day's arc, 0 at midnight to 1 at the next midnight. */
export function dayArc(d: Date = new Date()): number {
  return (d.getHours() * 60 + d.getMinutes()) / 1440;
}

/** "2:15 pm" in the reader's locale, lower-case meridian, no leading zero. */
export function clockLabel(d: Date): string {
  return d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }).replace(/\s?(am|pm)$/i, (m) => ` ${m.trim().toLowerCase()}`);
}

/** "Set at 2:15 pm", or "Set 12 minutes ago" once the page is older than a minute or two. */
export function setAtLabel(atSec: number, nowMs: number = Date.now()): string {
  if (!atSec) return "";
  const ageMin = Math.round((nowMs - atSec * 1000) / 60000);
  if (ageMin >= 2 && ageMin < 60) return `Set ${ageMin} minutes ago`;
  return `Set at ${clockLabel(new Date(atSec * 1000))}`;
}

/** Short age for a kicker: "just now", "12 min ago", "3 h ago", "yesterday". */
export function agoShort(iso: string | number, nowMs: number = Date.now()): string {
  const t = typeof iso === "number" ? (iso > 1e12 ? iso : iso * 1000) : Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.round((nowMs - t) / 60000));
  if (min < 2) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d <= 1 ? "yesterday" : `${d} days ago`;
}

/** The page's ledger: how many stories, about how long, across how many publications. */
/** Stories, minutes and publications for the front page's masthead line. The minutes follow the
 *  one rule every surface uses (functions/_minutes.js), so the door, the home and the Edition
 *  never promise three different times for the same stories. */
export function paperStats(cards: InSnipCard[]): { stories: number; minutes: number; sources: number } {
  const stories = cards.length;
  const domains = new Set<string>();
  const host = (u?: string | null) => {
    if (!u) return "";
    try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return ""; }
  };
  for (const c of cards) {
    const p = host(c.primarySourceUrl);
    if (p) domains.add(p);
    for (const s of c.supportingSources ?? []) {
      const d = host(s.url);
      if (d) domains.add(d);
    }
  }
  return { stories, minutes: readMinutes(stories), sources: domains.size };
}

/** Sections, the way a paper is divided: stories grouped by category, the strongest section
 *  first (by the rank of its best story), each in page order. Categories with one story fold
 *  into "Also today" at the end so no section is a lone column. */
export function paperSections<T extends { category?: string }>(
  cards: T[],
  minPerSection = 2,
): { name: string; cards: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const c of cards) {
    const name = typeof c.category === "string" && c.category ? c.category : "Also today";
    const list = groups.get(name) ?? [];
    list.push(c);
    groups.set(name, list);
  }
  const out: { name: string; cards: T[] }[] = [];
  const loose: T[] = [];
  for (const [name, list] of groups) {
    if (list.length >= minPerSection && name !== "Also today") out.push({ name, cards: list });
    else loose.push(...list);
  }
  // Page order is rank order, so the first story of each section decides where it sits.
  out.sort((a, b) => cards.indexOf(a.cards[0]) - cards.indexOf(b.cards[0]));
  if (loose.length) {
    loose.sort((a, b) => cards.indexOf(a) - cards.indexOf(b));
    out.push({ name: "Also today", cards: loose });
  }
  return out;
}

/** The publication's name for a kicker, spelt the one way every surface spells it (the shared
 *  masthead map): "ABC News" for "abc-just-in", "The Nightly" for a thenightly.com.au link, and a
 *  story with neither a name nor a known host says nothing rather than something ugly. */
export function sourceName(name?: string | null, url?: string | null): string {
  const n = (name ?? "").trim();
  if (!n && !url) return "";
  if (/google.?trends|insnip/i.test(n)) return "";
  return sourceDisplayName(n, url);
}
